import axios, { AxiosResponse } from 'axios';
import { NavigateFunction } from 'react-router-dom';
import io from 'socket.io-client';

import * as forage from './forage';
import { createSysMessage, Data, IMessage, MIME, Mode } from './message';
import { Profile } from './profile';

export type UpchatMessage = IMessage;
export type MessageMode = Mode;

export enum MATCH_ERR_CODE {
  NOT_MATCHED = 0,
  NO_ONLINE_WAITER = -1,
}
type Observer = (client: UpchatClient) => void;

function createFormData(file: File | Blob) {
  const result = new FormData();
  result.append('file', file);
  return result;
}

interface UpchatClientOptions {
  navigate: NavigateFunction;
  websocketURL: string;
  serviceURl: string;
}

interface UploadResponse {
  status?: string;
  errors?: Array<any>;
  name?: string;
  size?: number;
  type?: string;
  url?: string;
}

export class UpchatClient {
  constructor(options: UpchatClientOptions) {
    this.serviceURl = options.serviceURl;
    this.websocketURL = options.websocketURL;
    this.navigate = options.navigate;
  }

  connect = async () => {
    // 将本地的用户数据同步到内存中
    await this.profile.sync();
    this.io = io.io(this.websocketURL, {
      query: {
        fingerprint: this.profile.fingerprint!,
        environment: this.profile.environment!,
        referrer: document.referrer,
        username:
          new URL(window.location.href).searchParams.get('username') || '',
      },
    });
    this.io?.on('connect', this.ioDidConnect);
    this.io?.on('disconnect', this.ioDidDisconnect);
    this.io?.on('sysret.match', this.sysretMatched);
    this.io?.on('sysret.exit', this.sysretExit);
    this.io?.on('sysret.beMoved', this.sysretBeMoved);
    this.io?.on('sysret.sync', this.sysretSync);
    this.io?.on('sysret.alert', this.alert);
    this.io?.on('sysret.busy', this.sysretBusy);
    // TODO
    this.io?.on('sysret.delete', this.onMessageDelFunc);
    this.io?.on('message', this.onMessageFunc);
  };

  io: io.Socket | null = null;
  readonly websocketURL: string;
  readonly serviceURl: string;
  readonly navigate: NavigateFunction;
  readonly observers: Observer[] = [];

  // 用户信息
  profile: Profile = new Profile();
  remoteWaiter: any;

  // 会话id
  channelId: number = MATCH_ERR_CODE.NOT_MATCHED;

  // 接收信息时触发
  private onMessageFunc: any;
  private onMessageDelFunc: any;

  sendMessageCount: number = 0;

  onMessage(fn: (types: 'direct' | 'backflow', message: UpchatMessage) => any) {
    this.onMessageFunc = fn;
  }

  onMessageDel(fn: (messageId: number) => any) {
    this.onMessageDelFunc = fn;
  }

  // @ts-ignore
  send = (data: Data) => {
    if (!this.io?.connected) {
      return alert('已断开连接，请刷新页面');
    }
    if (!this.remoteWaiter || !this.channelId) {
      return alert('当前没有客服，请刷新页面重新连接');
    }
    this.sendMessageCount++;
    data.created_at = new Date().toISOString();
    data.mode = Mode.O_NORMAL;
    data.channel_id = this.channelId;
    const destSocketId = this.remoteWaiter.socketId;
    this.io?.emit('channels.messages.add', { data, destSocketId });
  };

  sendFile = async (file: File | Blob) => {
    let res;
    try {
      res = await this.upload(file);
      if (!res || !res.data || !res.data.url || res.data.status === 'fail') {
        return alert(res.data?.errors?.[0]?.message || '上传失败');
      }
    } catch (e) {
      console.log(e);
      return alert('上传失败');
    }
    this.send({
      content_type: file.type as MIME,
      content: res.data.url,
    });
  };

  // upload 将文件上传到云存储空间，返回它的url
  upload(file: File | Blob): Promise<AxiosResponse<UploadResponse>> {
    return axios.post(`${this.serviceURl}/upload`, createFormData(file));
  }

  private ioDidDisconnect = () => {
    if (!this.onMessageFunc) return;
    this.onMessageFunc(
      'direct',
      createSysMessage(
        this.profile,
        `系统提示：非常抱歉，网络异常，您已断开连接... - ${new Date().toLocaleString(
          'zh',
          { hour12: false }
        )}`
      )
    );
  };

  private ioDidConnect = () => {
    this.publish();
  };

  private alert(msg: string) {
    return alert(msg);
  }

  // 订阅函数
  subscribe(fn: Observer) {
    this.observers.push(fn);
  }

  // 触发订阅函数
  publish = () => {
    this.observers.forEach((v) => v(this));
  };

  get connectStatus() {
    if (!this.io?.connected) return '未连接';
    if (!this.remoteWaiter) return '等待客服';
    return '客服-' + this.remoteWaiter.name;
  }

  loadHistoryMessages() {
    return forage.getHistoryMessages();
  }

  // 评价
  syscallSubmitOpinion(
    data: Record<'score', number> & Record<'content', string | null>
  ) {
    this.io?.emit('channels.opinions.add', {
      opinion_score: data.score,
      opinion_content: data.content,
    });

    if (data.content) {
      setTimeout(
        () =>
          this.send({
            content_type: MIME['text/plain'],
            content: '用户评价内容: ' + data.content,
          }),
        500
      );
    }
  }

  // 提交用户信息表单
  syscallSubmitUserInfo = (data: string) => {
    this.io?.emit('users.anonymous.set', {
      id: this.profile.id,
      nickname: data,
    });
    setTimeout(
      () =>
        this.send({
          content_type: MIME['text/plain'],
          content: '用户信息: ' + data,
        }),
      500
    );
  };

  syscallSubmitAdvisoryType = (data: string) => {
    this.send({
      content_type: MIME['text/plain'],
      content: data,
    });
    this.io?.emit('channels.ashtype.set', {
      channelId: this.channelId,
      askType: data,
    });
  };

  // 反馈建议
  syscallSubmitFeedback = (data: any) => {
    console.log(data);
    if (!this.channelId) return;

    const { files, userinfo, description, contact } = data;

    this.send({
      content_type: MIME['text/plain'],
      content: `问题及建议: ${description}\n用户信息: ${userinfo}\n联系方式: ${contact}`,
    });
    for (const file of files) {
      this.sendFile(file);
    }
  };

  // 反馈建议
  syscallSubmitOfflineMsg = async (data: any) => {
    console.log(data);
    const { userinfo, description, contact } = data;

    const files = [];
    try {
      for (const file of data.files) {
        const res = await this.upload(file);
        if (!res || !res.data || !res.data.url) {
          return alert('文件上传失败，请重试！');
        } else files.push(res.data.url);
      }
    } catch (e) {
      console.log(e);
      return alert('文件上传失败，请重试！');
    }

    return axios.post(`${this.serviceURl}/feedbacks`, {
      userinfo,
      description,
      contact,
      files: files.join('|'),
      anonymouId: this.profile.id,
    });
  };

  syscallSubmitProblem(data: any) {
    const content = ['用户信息: ' + data.userInfo, '问题描述：' + data.problem];
    return this.send({
      content_type: MIME['text/plain'],
      content: content.join('\n'),
    });
  }

  // 处理会话被转接
  private sysretBeMoved = (source: any, dest: any) => {
    this.remoteWaiter = dest;
  };

  // 处理客服切换为忙碌
  private sysretBusy = (user: any) => {
    if (
      this.remoteWaiter?.socketId === user.socketId &&
      this.sendMessageCount === 0
    ) {
      this.io?.disconnect();
    }
  };

  // 处理客服离线
  private sysretExit = (user: any) => {
    if (this.remoteWaiter?.socketId === user.socketId) {
      this.remoteWaiter = null;
      this.channelId = MATCH_ERR_CODE.NOT_MATCHED;
    }
  };

  // 处理顾客信息同步
  private sysretSync = (profile: any, _: any) => {
    this.profile.save(profile);
  };

  // 被匹配时触发
  private sysretMatched = (channelId: number, profile: any) => {
    if (channelId === MATCH_ERR_CODE.NO_ONLINE_WAITER) {
      this.navigate('/agent?redirect=1');
    }
    this.remoteWaiter = profile;
    this.channelId = channelId;
    this.publish();
  };
}
